import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { eUserStatus } from '@ministrary/shared/enums/user-status.enum';
import { AuthService } from '@ministrary/shared/services/auth/auth.service';
import { AccessBlockService } from '@ministrary/shared/services/access-block/access-block.service';

import { ChurchService } from '../services/church/church.service';
import { PermissionService } from '../services/permission/permission.service';

export const AUTH_REDIRECT_URL = '/auth';
export const UNAUTHORIZED_REDIRECT_URL = '/unauthorized';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  private accessBlockService = inject(AccessBlockService);
  private authService = inject(AuthService);
  private churchService = inject(ChurchService);
  private permissionService = inject(PermissionService);
  private router = inject(Router);

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.handleAuthentication(next, state);
  }

  canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.handleAuthentication(next, state);
  }

  canLoad(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.handleAuthentication(next, state);
  }

  handleAuthentication(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (this.accessBlockService.isBlocked()) {
      return false;
    }

    const isAuthenticated = this.authService.isAuthenticated;
    if (!isAuthenticated) {
      this.router.navigate([AUTH_REDIRECT_URL], { queryParams: { returnUrl: state.url } });
      return false;
    }

    const currentUser = this.authService.currentUser;
    if (currentUser.status === eUserStatus.pending) return !this.navigateTo('/complete-registration');
    if (currentUser.reset_password) return !this.navigateTo('/reset-password');

    if (!this.churchService.selectedChurch()) return !this.navigateTo('/link-church');

    if (!route.data) return true;

    const requiredProducts = route.data['products'];
    if (requiredProducts) {
      const hasRequiredProduct = this.churchService.hasProduct(requiredProducts);
      if (!hasRequiredProduct) {
        this.navigateTo(UNAUTHORIZED_REDIRECT_URL);
        return false;
      }
    }

    const requiredRoles = route.data['roles'];
    const allowSupport = !!route.data['allowSupport'];
    const requiredMinistryRoles = route.data['ministryRoles'];
    if (!requiredRoles && !requiredMinistryRoles && !allowSupport) return true;

    const hasRequiredRole = this.authService.hasRole(requiredRoles);
    const hasSupportAccess = allowSupport && this.authService.isSupport;
    const hasRequiredMinistryRole = this.authService.hasMinistryRole(requiredMinistryRoles);
    if (!hasRequiredRole && !hasSupportAccess && !hasRequiredMinistryRole) {
      this.navigateTo(UNAUTHORIZED_REDIRECT_URL);
      return false;
    }

    return true;
  }

  private navigateTo(route: string) {
    this.router.navigate([route], { queryParamsHandling: 'merge' });
    return false;
  }
}
